import { addAction, addError } from "@wayflyer/blackbox-fe";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Outlet, createBrowserRouter, useRouteError, type LoaderFunction, type RouteObject } from "react-router";
import { ErrorComponent, getCustomerErrorPresetMap, getErrorVariant } from "shared-ui";
import { useIntercomLoaded } from "./libs/app/intercom";

import { Fallback } from "./fallback";
import { ErrorPage, RootComponent } from "./libs/app/renderRootComponent";
import MetaServerError from "./libs/meta-server-error";

const chunkLoadingErrorMsgs = [
  "Failed to fetch dynamically imported module", // Chrome
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
];

const fiveMinutes = 5 * 60 * 1000;

const loopProtectedReloadKey = "wf-last-reload-time";

const loopProtectedReload = (error: TypeError) => {
  const lastReloadTime = sessionStorage.getItem(loopProtectedReloadKey);

  if (lastReloadTime && Date.now().valueOf() - Number(lastReloadTime) < fiveMinutes) {
    addError("Attempted to reload due to chunk loading error within five minutes", { cause: error });
    return;
  }

  sessionStorage.setItem(loopProtectedReloadKey, Date.now().valueOf().toString());
  window.location.reload();
};

const ErrorElement = () => {
  const error = useRouteError();
  const [component, setComponent] = useState<JSX.Element | null>(null);
  useIntercomLoaded();

  useEffect(() => {
    if (error instanceof MetaServerError) {
      const errorMap = {
        400: () => import("./errors/error400"),
        403: () => import("./errors/error403"),
        404: () => import("./errors/error404"),
        500: () => import("./errors/error500"),
      };

      const errorVariantMap = {
        authnAccountDeactivated: () => import("./errors/errorAuthnAccountDeactivated"),
        authnEmailConstraint: () => import("./errors/errorAuthnEmailConstraint"),
      };

      if (error.status in errorMap) {
        const loadErrorComponent = async () => {
          const hasVariant = error.variant in errorVariantMap;
          const errorModuleLoader = hasVariant ? errorVariantMap[error.variant]() : errorMap[error.status]();
          const { Component } = await errorModuleLoader;
          setComponent(() => <Component />);
        };
        loadErrorComponent();
      }
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setComponent(() => <DefaultErrorElement error={error} />);
    }
    MetaServerError.cleanup(); // Clean up the `wayflyer:status-code` meta tag after use
  }, [error]);

  return component;
};

const DefaultErrorElement = (props: { error: any }) => {
  const { error } = props;

  const variant = getErrorVariant(error);
  const intl = useIntl();
  const { title, message, footerText, primaryAction } = getCustomerErrorPresetMap({ intl })[variant];

  useEffect(() => {
    // @todo: this is a temporary solution to handle chunk loading errors
    // Remove when JS assets are deployed to S3 and not served from Django
    if (error instanceof TypeError && chunkLoadingErrorMsgs.some((msg) => error.message.includes(msg))) {
      addAction("Failed to fetch dynamically imported module, reloading", { cause: error });
      loopProtectedReload(error);
    } else {
      addError(error);
    }
  }, [error]);

  return (
    <ErrorComponent
      {...{
        title,
        message,
        footerText,
        primaryAction,
        secondaryAction: {
          content: intl.formatMessage({ id: "error.goBack", defaultMessage: "Go back" }),
          onAction: () => window.history.back(),
        },
      }}
    />
  );
};

const rootLoader: LoaderFunction = () => {
  const metaError = MetaServerError.fromMeta();
  if (metaError !== null) {
    throw metaError;
  }
};

// Exported so tests can mount the real route tree under their own shell.
export const routes: RouteObject[] = [
  {
    path: "/",
    handle: {
      title: "Wayflyer",
    },
    loader: rootLoader,
    element: (
      <RootComponent>
        <Outlet />
      </RootComponent>
    ),
    errorElement: (
      <ErrorPage>
        <ErrorElement />
      </ErrorPage>
    ),
    HydrateFallback: Fallback,
    children: [
      {
        path: "/",
        lazy: () => import("./entry-points/home"),
        handle: {
          title: "Home",
        },
      },
      {
        // TODO: things under /auth will be handled on the server side
        // and as such, should not render UI, just redirect
        path: "/auth",
        children: [
          {
            path: "bigcommerce/load",
            lazy: () => import("./integrations/bigcommerce-iframe/bigcommerce-load"),
            handle: {
              title: "Wayflyer for BigCommerce",
            },
          },
          {
            path: "bigcommerce/auth",
            lazy: () => import("./integrations/bigcommerce-iframe/bigcommerce-auth"),
            handle: {
              title: "Wayflyer for BigCommerce",
            },
          },
        ],
      },
      {
        path: "/authn-wf/callback",
        lazy: () => import("./authn/callback"),
      },
      {
        path: "/authn-wf/callback-transaction",
        lazy: () => import("./authn/callback-transaction"),
      },
      // ===> Local development only: uncomment/see for example of authn utils.
      // {
      //   path: "/authn-wf/example",
      //   lazy: () => import("./authn/example"),
      // },
      // {
      //   path: "/authn-wf/example-hook",
      //   lazy: () => import("./authn/example-hook"),
      // },
      // <======================================================================
      {
        path: "/beta-signup-wfbank",
        lazy: async () => {
          const { Component } = await import("./product-choice");
          return {
            Component: () => <Component product="banking" />,
          };
        },
        handle: {
          title: "Banking",
        },
      },
      {
        path: "/intelligence",
        lazy: async () => {
          const { Component } = await import("./product-choice");
          return {
            Component: () => <Component product="intelligence" />,
          };
        },
        handle: {
          title: "Intelligence",
        },
      },
      {
        path: "/company-creation/*",
        lazy: () => import("./entry-points/company_creation/app"),
        handle: {
          title: "Company Details",
        },
      },
      {
        path: "/documents",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Documents",
        },
      },
      {
        path: "/customer-referrals",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Customer Referrals",
        },
      },
      {
        path: "/financial-os-banking",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Financial OS Banking",
        },
      },
      {
        path: "/financing/ach-stripe",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "ACH Stripe",
        },
      },
      {
        path: "/financing/bank-statements",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Upload Bank Statements",
        },
      },
      {
        path: "/financing/bank-statements/connect",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Connect Bank Statements",
        },
      },
      {
        path: "/financing/bank-statements/connect/basiq",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Connect Bank Statements via Basiq",
        },
      },
      {
        path: "/financing/bank-statements/upload",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Upload Bank Statements",
        },
      },
      {
        path: "/financing/funds-request",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Funds Request",
        },
      },
      {
        path: "/funding/how-to-apply",
        lazy: () => import("./entry-points/funding_application/how-to-apply-app"),
        handle: {
          title: "Apply for Funding",
        },
      },
      {
        path: "/funding/wayflyer-hosted-capital",
        lazy: () => import("./entry-points/hosted-capital/app"),
        handle: {
          title: "Wayflyer Hosted Capital",
        },
      },
      {
        path: "/settings-connections",
        lazy: async () => {
          const { Component } = await import("./authn/authenticated-customer-route");
          return {
            Component: () => <Component alternativePath="/settings/connections" />,
          };
        },
        handle: {
          title: "Connect your store",
        },
      },
      {
        path: "/settings/connections/connect-accounting",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Connect your store",
        },
      },
      {
        // Slug-less landing target for the Plaid OAuth return on the new company
        // connections page. Plaid redirect URIs are registered without the customer
        // slug, so this recovers the customer and forwards (preserving oauth_state_id)
        // to /:customerDomain/settings/company/connections.
        path: "/settings/company/connections",
        lazy: () => import("./authn/authenticated-customer-route"),
        handle: {
          title: "Connections",
        },
      },
      {
        path: "/upload-documents",
        lazy: async () => {
          const { Component } = await import("./authn/authenticated-customer-route");
          return {
            Component: () => <Component alternativePath="/funding/upload-documents" />,
          };
        },
        handle: {
          title: "Upload Documents",
        },
      },
      {
        path: "/verify-identity/:idv_token",
        lazy: () => import("./entry-points/kyc/verify-identity"),
        handle: {
          title: "Verify your identity",
        },
      },
      {
        path: "/verify-identity/v2/:idv_token",
        lazy: () => import("./entry-points/kyc-v3/verify-identity-unauthenticated"),
        handle: {
          title: "Verify your identity",
        },
      },
      {
        path: "/:customerDomain/verify-identity/:associated_person_id",
        lazy: () => import("./entry-points/kyc-v3/verify-identity-authenticated"),
        handle: {
          title: "Verify your identity",
        },
      },
      {
        path: "/:customerDomain/?",
        lazy: () => import("./entry-points/home/customer-home"),
        handle: {
          title: "Home",
        },
      },
      {
        path: "/:customerDomain/banking/",
        lazy: () => import("./entry-points/banking-app/banking-application"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: "/:customerDomain/banking/mfa",
        lazy: () => import("./banking/banking-mfa-setup-screen/banking-mfa-setup"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: "/:customerDomain/banking/*",
        lazy: () => import("./entry-points/banking-app/app"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: "/:customerDomain/beta-signup-wfbank",
        lazy: () => import("./entry-points/banking-app/bank-beta-signup"),
        handle: {
          title: "Banking",
        },
      },
      {
        path: "/:customerDomain/connections",
        lazy: () => import("./settings/connections-redirect"),
        handle: {
          title: "Connect your store",
        },
      },
      {
        path: "/:customerDomain/customer-referrals",
        lazy: () => import("./customer-referrals/customer-referrals-screen/customer-referrals-screen"),
        handle: {
          title: "Customer Referrals",
        },
      },
      {
        path: "/:customerDomain/bookkeeping/waitlist/",
        lazy: () => import("./bookkeeping/bookkeeping-waitlist-screen"),
        handle: {
          title: "Bookkeeping waitlist",
        },
      },
      {
        path: "/:customerDomain/financing-contract-order-form",
        lazy: () => import("./financing/contracts/screens/mca-order-form-screen/mca-order-form-screen"),
        handle: {
          title: "Financing Contract Order Form",
        },
      },
      {
        path: "/:customerDomain/financing-contract-disclosures",
        lazy: () => import("./financing/contracts/screens/disclosure-screen/disclosure-screen"),
        handle: {
          title: "Financing Contract Disclosures",
        },
      },
      {
        path: "/:customerDomain/financing-contract-bank-details",
        lazy: () => import("./financing/contracts/screens/bank-account-info-screen/bank-account-info-screen"),
        handle: {
          title: "Financing Contract Bank Details",
        },
      },
      {
        path: "/:customerDomain/financing/reconnect/*?",
        lazy: () => import("./financing/reconnect"),
        handle: {
          title: "Reconnect platforms",
        },
      },
      {
        path: "/:customerDomain/financing/*",
        lazy: () => import("./financing/financing"),
        handle: {
          title: "Financing",
        },
      },
      {
        path: "/:customerDomain/financing/ach-stripe",
        lazy: () => import("./financing/bank-connect/ach-stripe/ach-stripe"),
      },
      {
        path: "/:customerDomain/financing/bank-statements",
        lazy: () => import("./financing/upload-bank-statements"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: "/:customerDomain/financing/bank-statements/connect",
        lazy: () => import("./financing/upload-bank-statements"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: "/:customerDomain/financing/bank-statements/upload",
        lazy: () => import("./financing/bank-statements-upload/bank-statements-upload-screen"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: "/:customerDomain/financing/direct-debit/:ppAccountName",
        lazy: () => import("./financing/direct-debit/direct-debit"),
        handle: {
          title: "Upload your bank statements",
        },
      },
      {
        path: "/:customerDomain/financing/funds-request/*",
        lazy: () => import("./financing/funds-request"),
        handle: {
          title: "Financing",
        },
      },
      {
        path: "/:customerDomain/financing/statements",
        lazy: () => import("./financing/statements"),
        handle: {
          title: "Generate transaction statement",
        },
      },
      {
        path: "/:customerDomain/financing/write-back",
        lazy: () => import("./financing/write-back/write-back"),
        handle: {
          title: "Xero Sync Integration",
        },
      },
      {
        path: "/:customerDomain/funding/application/*?",
        lazy: () => import("./entry-points/funding_application/app"),
        handle: {
          title: "Apply for Funding",
        },
      },
      {
        // This is temporary while under development. Will be moved into customer-home once ready for release.
        path: "/:customerDomain/home",
        lazy: () => import("./home"),
        handle: {
          title: "Home",
        },
      },
      {
        path: "/:customerDomain/invites/grant-access/:requestToken",
        lazy: () => import("./entry-points/company_creation/invite-request"),
        handle: {
          title: "Invite Requests",
        },
      },
      {
        path: "/:customerDomain/performance",
        lazy: () => import("./entry-points/insights/overview-dashboard"),
        handle: {
          title: "Performance - Overview",
        },
      },
      {
        path: "/:customerDomain/performance/channels",
        lazy: () => import("./entry-points/insights/channels"),
        handle: {
          title: "Performance - Channels",
        },
      },
      {
        path: "/:customerDomain/performance/googleads",
        lazy: () => import("./entry-points/insights/googleads"),
        handle: {
          title: "Performance - Google Ads",
        },
      },
      {
        path: "/:customerDomain/performance/int-overview",
        lazy: () => import("./entry-points/insights/internal-overview-dashboard"),
        handle: {
          title: "Performance - Overview",
        },
      },
      {
        path: "/:customerDomain/performance/ltv",
        lazy: () => import("./entry-points/insights/consumers"),
        handle: {
          title: "Lifetime Value",
        },
      },
      {
        path: "/:customerDomain/performance/ltv-cac",
        lazy: () => import("./entry-points/insights/internal-ltv-cac"),
        handle: {
          title: "Performance - LTV-CAC",
        },
      },
      {
        path: "/:customerDomain/performance/metaads",
        lazy: () => import("./entry-points/insights/meta-ads"),
        handle: {
          title: "Performance - Meta Ads",
        },
      },
      {
        path: "/:customerDomain/performance/facebook",
        lazy: () => import("./entry-points/insights/facebook"),
        handle: {
          title: "Performance - Meta Ads",
        },
      },
      {
        path: "/:customerDomain/performance/paid-channel-summary",
        lazy: () => import("./entry-points/insights/paid-channel-summary"),
        handle: {
          title: "Paid Channels",
        },
      },
      {
        path: "/:customerDomain/performance/products",
        lazy: () => import("./entry-points/insights/products"),
        handle: {
          title: "Performance - Products",
        },
      },
      {
        path: "/:customerDomain/performance/revenue",
        lazy: () => import("./entry-points/insights/internal-revenue"),
        handle: {
          title: "Performance - Revenue",
        },
      },
      {
        path: "/:customerDomain/performance/tiktok-spend",
        lazy: () => import("./entry-points/insights/internal-tiktok-ad-spend"),
        handle: {
          title: "Performance - TikTok Ad Spend",
        },
      },
      {
        path: "/:customerDomain/intelligence/*",
        lazy: () => import("./entry-points/intelligence/chat"),
        handle: {
          title: "Intelligence",
        },
      },
      {
        path: "/:customerDomain/remediation/*",
        lazy: () => import("./entry-points/company_creation/remediation"),
        handle: {
          title: "Company Remediation",
        },
      },
      {
        path: "/:customerDomain/settings/team-member/:memberId",
        lazy: () => import("./entry-points/profile-spaces/edit-team-member"),
        handle: {
          title: "Edit Team Member",
        },
      },
      {
        // Its own screen, not part of connections settings. Outranks the route below.
        path: "/:customerDomain/settings/connections/reconnect/*",
        lazy: () => import("./settings/reconnect-app"),
        handle: {
          title: "Reconnect platforms",
        },
      },
      {
        path: "/:customerDomain/settings/connections/*",
        lazy: () => import("./settings/app"),
        handle: {
          title: "Connect your store",
        },
      },
      {
        // Profile-pages IA (PFF-472). The whole section is ONE lazy chunk: both
        // sections import the same `profile-spaces/app` module (each picking its
        // own sub-router export), and each sub-router uses a descendant <Routes>
        // for its screens. The account section is user-scoped, so it's mounted
        // domain-less; the company section stays under `/:customerDomain/` since
        // it needs a company.
        path: "/settings/account/*",
        lazy: async () => ({ Component: (await import("./entry-points/profile-spaces/app")).AccountApp }),
        handle: {
          title: "Account",
        },
      },
      {
        // Splat-mounted per section (not /settings/*) so the sibling
        // /settings/team-member and /settings/connections routes keep matching.
        path: "/:customerDomain/settings/company/*",
        lazy: async () => ({ Component: (await import("./entry-points/profile-spaces/app")).CompanyApp }),
        handle: {
          title: "Company",
        },
      },
      {
        path: "/:customerDomain/verify-business",
        lazy: () => import("./entry-points/kyc/verify-business"),
        handle: {
          title: "Verify your business",
        },
      },
      {
        path: "/:customerDomain/verify-company",
        lazy: () => import("./entry-points/kyc-v3/verify-company"),
        handle: {
          title: "Verify your company",
        },
      },
      {
        path: "/:customerDomain/verify-business/additional-information-request/:kycAirId",
        lazy: () => import("./entry-points/kyc/verify-business-air"),
        handle: {
          title: "Verify your identity",
        },
      },
      {
        path: "/400",
        lazy: () => import("./errors/error400"),
        handle: {
          title: "Bad request",
        },
      },
      {
        path: "/403",
        lazy: () => import("./errors/error403"),
        handle: {
          title: "Access denied",
        },
      },
      {
        path: "/404",
        lazy: () => import("./errors/error404"),
        handle: {
          title: "Page not found",
        },
      },
      {
        path: "/500",
        lazy: () => import("./errors/error500"),
        handle: {
          title: "Error",
        },
      },
      {
        path: "/authn-wf/account-deactivated",
        lazy: () => import("./errors/errorAuthnAccountDeactivated"),
        handle: {
          title: "Account deactivated",
        },
      },
      {
        path: "/authn-wf/email-constraint",
        lazy: () => import("./errors/errorAuthnEmailConstraint"),
        handle: {
          title: "Login method error",
        },
      },
      {
        path: "/authn-wf/error",
        lazy: () => import("./errors/errorAuthnGeneric"),
        handle: {
          title: "Authentication error",
        },
      },
      {
        path: "*",
        lazy: async () => {
          const { Component } = await import("./errors/error404");
          return {
            Component: () => <Component withLogging />,
          };
        },
        handle: {
          title: "Page not found",
        },
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
